"use client";

import { useEffect, useMemo, useState } from "react";
import { css } from "styled-system/css";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

export type ClientRow = { clientId: string | null; email: string | null; displayName: string; phone: string | null };

/** Carga la lista de clientes del CRM para autocompletar. `enabled` controla cuándo disparar el fetch (ej: modal abierto). */
export function useClientList(enabled: boolean): ClientRow[] {
  const { refresh } = useAuth();
  const [clients, setClients] = useState<ClientRow[]>([]);

  useEffect(() => {
    if (!enabled) return;
    void fetchWithSessionSync("/api/dashboard/clients", { credentials: "include" }, refresh)
      .then(async (r) => (r.ok ? ((await r.json()) as ClientRow[]) : []))
      .then(setClients)
      .catch(() => setClients([]));
  }, [enabled, refresh]);

  return clients;
}

/**
 * Buscador de clientes existentes por nombre/email/teléfono, con dropdown de resultados.
 * Al seleccionar un cliente dispara `onSelect`; el llamador decide cómo prellenar sus campos.
 */
export function ClientSearchAutocomplete({
  clients,
  onSelect,
  disabled = false,
  label = "Buscar cliente existente",
  placeholder = "Nombre, email o teléfono...",
}: {
  clients: ClientRow[];
  onSelect: (client: ClientRow) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}) {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients
      .filter(
        (c) =>
          c.displayName.toLowerCase().includes(q) ||
          (c.email ?? "").toLowerCase().includes(q) ||
          (c.phone && c.phone.includes(q)),
      )
      .slice(0, 8);
  }, [clients, search]);

  return (
    <div className={css({ position: "relative" })}>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>{label}</span>
        <input
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          placeholder={placeholder}
          autoComplete="off"
          className={css({
            h: "10",
            px: "3",
            fontSize: "sm",
            color: "#0f172a",
            borderWidth: "1px",
            borderColor: "#dbe2ea",
            rounded: "lg",
            bg: "#f8fafc",
            width: "100%",
            _focus: { outline: "none", bg: "white", borderColor: "#4f46e5" },
            _disabled: { bg: "#f1f5f9", color: "#94a3b8", cursor: "not-allowed" },
          })}
        />
      </label>

      {showDropdown && (search.trim() || filtered.length > 0) && (
        <div
          className={css({
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: "1",
            bg: "white",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            rounded: "lg",
            shadow: "md",
            zIndex: "50",
            overflow: "hidden",
            maxH: "48",
            overflowY: "auto",
          })}
        >
          {filtered.map((c) => (
            <button
              key={c.clientId ?? c.email}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(c);
                setSearch("");
                setShowDropdown(false);
              }}
              className={css({
                display: "flex",
                flexDir: "column",
                gap: "0.5",
                w: "100%",
                px: "3",
                py: "2",
                textAlign: "left",
                cursor: "pointer",
                _hover: { bg: "#f1f5f9" },
              })}
            >
              <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>{c.displayName}</span>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>
                {c.email}
                {c.phone ? ` · ${c.phone}` : ""}
              </span>
            </button>
          ))}
          {filtered.length === 0 && search.trim() && (
            <p className={css({ px: "3", py: "2", fontSize: "sm", color: "#94a3b8" })}>
              Sin resultados. Completá los campos abajo para el nuevo cliente.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
